import * as React from 'react';
import { useMemo } from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { useDispatch } from 'react-redux';

import { Checkbox } from '~shared/components/Checkbox';
import { Divider } from '~shared/components/Divider';
import { Student } from '~teacher/models/reports';
import { selectStudent } from '~teacher/slices/emailReports/emailReportsSlice';

import { Pending } from '../ExportReports/ExportFeedback/ExportFeedbackIcons';

interface Props {
  students: Student[];
  isRequestInProgress: boolean;
  handleBackClicked: () => void;
}

interface StudentProps {
  student: Student;
}

const EmailStudentsSelectableRecipient: React.FC<StudentProps> = ({
  student,
}: React.PropsWithChildren<StudentProps>) => {
  const dispatch = useDispatch();
  const { isSelected, email, hasParticipated, id } = student;
  let studentEmailText;

  if (email) {
    studentEmailText = hasParticipated
      ? email
      : translate(`didn't participate`);
  } else {
    studentEmailText = translate('no email');
  }

  const isDisabled = !(email && hasParticipated);
  const studentClass = useMemo(() => {
    return cx(
      'email-students-modal__selectable-recipient',
      'flex items-center w-full px-4 py-2 sm:py-4 text-center leading-8 text-base',
      {
        'text-grey-500': isDisabled,
        'text-blue-900 cursor-pointer': !isDisabled,
        'hover:bg-grey-50 focus:bg-grey-50': !isDisabled && !isSelected,
        'bg-cyan-50 hover:bg-cyan-100 focus:bg-cyan-100':
          !isDisabled && isSelected,
      }
    );
  }, [isDisabled, isSelected]);

  const handleOnClick = (event) => {
    event.preventDefault();
    if (isDisabled) {
      return;
    }
    dispatch(selectStudent({ studentId: id }));
  };

  return (
    <li className="border-0 border-b border-solid border-grey-200 list-none">
      <label data-testid="selectable-recipient" className={studentClass}>
        <Checkbox
          id={`student-${id}`}
          size="large"
          checked={isSelected}
          disabled={!(email && hasParticipated)}
          onChange={handleOnClick}
        />
        <div className="w-full overflow-hidden md:flex md:justify-between">
          <div className="flex-1 ml-4 text-left truncate">{`${student.firstName} ${student.lastName}`}</div>
          <div className="flex-1 ml-4 md:ml-8 text-left truncate">
            {studentEmailText}
          </div>
        </div>
      </label>
    </li>
  );
};

const EmailStudentsRecipientsList: React.FC<Props> = ({
  students = [],
  isRequestInProgress,
  children,
}: React.PropsWithChildren<Props>) => {
  return (
    <div className="flex flex-col justify-between h-full min-h-0">
      {isRequestInProgress ? (
        <div className="flex h-full items-center justify-center">
          <Pending />
        </div>
      ) : (
        <div className="pt-8 flex-1 min-h-0">
          <Divider />
          <div className="h-full overflow-y-auto scrolling-touch">
            <ul className="m-0 p-0 pb-8">
              {students.map((student: Student) => (
                <EmailStudentsSelectableRecipient
                  student={student}
                  key={student.id}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

export default EmailStudentsRecipientsList;
